import { FileDown, Loader2, ShieldCheck, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import type { TermDetail } from "@/api/db/terms";
import { Button } from "@/components/ui/button";
import { useTermIssueResolve, useTermPublish } from "@/hooks/use-terms";
import { errorCode } from "@/lib/error-code";
import { TermosLabels } from "@/lib/labels/termos";

const labels = TermosLabels.review;

export function ReviewBanner({ term }: { term: TermDetail }) {
	const pending = term.issues.filter((issue) => issue.resolution === null);

	return (
		<div className="mx-auto mt-4 w-full max-w-170 px-4">
			<div
				data-slot="term-review-banner"
				className="flex flex-col gap-3 rounded-lg border border-warning/40 bg-warning-soft px-4 py-3"
			>
				<div className="flex items-start gap-2">
					<TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />

					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-foreground">{labels.title}</p>

						{term.incomplete && (
							<p className="mt-0.5 text-xs text-foreground/80">{labels.incompleteBody}</p>
						)}

						{!term.incomplete && term.reviewRequired && pending.length === 0 && (
							<p className="mt-0.5 text-xs text-foreground/80">{labels.readyBody}</p>
						)}
					</div>
				</div>

				{pending.length > 0 && (
					<ul className="flex flex-col gap-2">
						{pending.map((issue) => (
							<li key={issue.id} className="rounded-md border border-warning/25 bg-card/70 p-3">
								<div className="flex flex-wrap items-center gap-2">
									<span className="text-xs font-semibold text-foreground">
										{labels.kinds[issue.kind]}
									</span>
									<span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
										{labels.severity[issue.severity]}
									</span>
								</div>

								<p className="mt-1 text-xs text-foreground/80">{issue.detail}</p>

								{issue.originalText !== null && issue.suggestedText !== null && (
									<div className="mt-2 grid gap-1 text-xs">
										<del className="text-destructive">{issue.originalText}</del>
										<ins className="text-whatsapp no-underline">{issue.suggestedText}</ins>
									</div>
								)}

								<ReviewIssueActions termId={term.id} issue={issue} />
							</li>
						))}
					</ul>
				)}

				<div className="flex flex-wrap items-center justify-between gap-2">
					<a
						href={`/term-generation/source/${term.id}`}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<FileDown className="size-3.5" aria-hidden />
						{labels.source}
					</a>

					{term.reviewRequired && (
						<ReviewPublishAction termId={term.id} disabled={pending.length > 0} />
					)}
				</div>
			</div>
		</div>
	);
}

function ReviewIssueActions({
	termId,
	issue,
}: {
	termId: number;
	issue: TermDetail["issues"][number];
}) {
	const { mutate, isPending } = useTermIssueResolve();
	const resolve = (action: "apply" | "accept" | "reject") => {
		mutate(
			{ termId, issueId: issue.id, action },
			{
				onSuccess: () => toast.success(labels.resolved),
				onError: (error) => {
					toast.error(errorCode(error) === "STALE_ISSUE" ? labels.stale : labels.error);
				},
			},
		);
	};

	return (
		<div className="mt-2 flex flex-wrap justify-end gap-2">
			<Button size="sm" variant="ghost" disabled={isPending} onClick={() => resolve("reject")}>
				{issue.patchable && labels.keep}
				{!issue.patchable && labels.dismiss}
			</Button>

			<Button
				size="sm"
				disabled={isPending}
				onClick={() => resolve(issue.patchable ? "apply" : "accept")}
			>
				{isPending && <Loader2 className="size-4 animate-spin" />}
				{issue.patchable && labels.apply}
				{!issue.patchable && labels.accept}
			</Button>
		</div>
	);
}

function ReviewPublishAction({ termId, disabled }: { termId: number; disabled: boolean }) {
	const { mutate, isPending } = useTermPublish();

	return (
		<Button
			size="sm"
			disabled={disabled || isPending}
			onClick={() =>
				mutate(
					{ termId },
					{
						onSuccess: () => toast.success(labels.published),
						onError: () => toast.error(labels.error),
					},
				)
			}
		>
			{isPending && <Loader2 className="size-4 animate-spin" />}
			{!isPending && <ShieldCheck />}
			{isPending && labels.publishing}
			{!isPending && labels.publish}
		</Button>
	);
}
